// The tower defence simulation. The enemy walks a road from the edge of the map to the Command
// Center; the player's towers stand either side of it and shoot what passes; whatever reaches the
// end wears the Command Center down, and when it is gone the game is over. Offshore, out of reach
// of anything the player builds, the enemy's fleet holds station and launches missiles and drones
// straight at the Command Center: the shield has to stop those in the air. Pure, deterministic and
// integer-only, like the raid sim it grew out of (see SPEC.md 5.2): the same seed and the same
// clicks give the same game, and its snapshots are the same shape, so the 3D scene draws them
// unchanged. The towers and the enemy are the defences and units of data.ts; their numbers, prices
// and rewards are in td-balance.ts, the roads in td-maps.ts and the waves in td-waves.ts.
import { MAP_SIZE, TICKS_PER_SECOND, TILE, dist, distToRect } from './fixed.ts';
import { SEA_WIDTH } from './grid.ts';
import { Rng } from './rng.ts';
import { PLACE_MARGIN } from './village.ts';
import { hpForLevel, interceptChanceVs, threatOf, type AbilityKind, type BuildingDef, type GameConfig, type Threat, type UnitDef } from './data.ts';
import type { CombatEvent, Shot, Snapshot, UnitSnapshot } from './types.ts';
import { HQ_SIZE, buildRoad, legDir, roadPoint, type Road, type TdMap } from './td-maps.ts';
import { BREACH, ENEMY_SPEED_PCT, TOWER_DAMAGE_PCT, rewardPct, PATROL_MAX_SPEED, PATROL_RESPAWN_TICKS, PATROL_TARGETS, FLEET_APPROACH, FLEET_SPEED_TPS, FLEET_STATION, EARLY_CALL_BONUS_PCT, MAX_TOWER_LEVEL, PRICE, REWARD, TD_CONFIG, TOWERS, WAVE_QUIET_TICKS, damageAt, rangeAt, rebuildPrice, sellValue, upgradePrice, waveBonus } from './td-balance.ts';
import { armourPct, tdWave, type TdShip, type TdWave } from './td-waves.ts';

/** Which enemy a tower shoots first: the one furthest down the road, the last, the toughest, or the nearest. */
export type TargetMode = 'first' | 'last' | 'strong' | 'close';
export const TARGET_MODES: readonly TargetMode[] = ['first', 'last', 'strong', 'close'];
export const TARGET_MODE_NAMES: Record<TargetMode, string> = { first: 'First', last: 'Last', strong: 'Strongest', close: 'Closest' };

export interface Tower {
  id: number;
  type: string;
  def: BuildingDef;
  x: number;          // tile
  y: number;
  level: number;
  hp: number;
  maxHp: number;
  /** Standing. A tower at 0 hp is rubble: still placed, firing nothing, until it is rebuilt. */
  alive: boolean;
  /** Sold or spent (a mine that went off): it is no longer on the map at all. */
  gone: boolean;
  cooldown: number;
  burstPos: number;
  ammo: number;
  targetId: number;
  /** Enemies an interceptor from this tower has already missed: it does not waste another on them. */
  missed: Set<number>;
  mode: TargetMode;
  priority: Threat[];
  /** It has fired this wave. */
  fired: boolean;
  /** The tick it last fired, for the turret animation. */
  firedTick: number;
}

export interface Enemy {
  id: number;
  type: string;
  def: UnitDef;
  /** How far along its road it is, in units, and which of the map's roads it walks. */
  s: number;
  lane: number;
  x: number;
  y: number;
  hp: number;
  /** Its hit points when it stepped onto the road: the waves past the hand-made ones come tougher. */
  maxHp: number;
  alive: boolean;
  /** It reached the Command Center. */
  through: boolean;
  /** Its place across the road, units either side of the centre line, so a column is not a queue. */
  off: number;
  cooldown: number;
  burstPos: number;
  /** The tower it is shooting at, or -1. */
  targetId: number;
  /** A warhead in its terminal dive: ticks until it lands, -1 for anything else. */
  fuse: number;
  /** Where it entered, and where a warhead is aimed, units: the arc it is drawn on. */
  spawnX: number;
  spawnY: number;
  lockX: number;
  lockY: number;
  /** Fired on: ticks it keeps jinking, and which way it breaks. */
  jinkTicks: number;
  jinkDir: number;
  /** What a warhead is going for; null for anything on the road. */
  aim: WarheadAim | null;
  /** A warhead that came down on the towers rather than the Command Center: neither a leak nor a kill. */
  landed: boolean;
  /** In the air, flying free of any lane straight at the tower with this id: 0 is the Command Center. -1 for anything on the road. */
  flyAt: number;
  /** The wave it came with: what its kill pays depends on it. */
  wave: number;
  /** The ship that launched it, by type; undefined for anything from beyond the map. */
  launcher?: string;
  /** Its own speed against the book's, in percent: no two in a column walk in step. */
  pace: number;
}

/** Where a warhead is going: the Command Center, or the towers it can hurt most. */
export type WarheadAim = 'hq' | 'towers';

/**
 * A ship of the enemy's fleet: it sails in from the open sea to its station, FLEET_STATION tiles
 * off the coast, launches what it carries at the Command Center one at a time, and sails back out.
 * Nothing the player builds reaches it, so it has no hit points to lose and is not an Enemy.
 */
export interface Ship {
  id: number;
  type: string;
  def: UnitDef;
  x: number;
  y: number;
  /** What it launches, and how many it has left. */
  unit: string;
  left: number;
  gap: number;
  /** Ticks until its next launch, once on station. */
  launchIn: number;
  /** Sailing in, on station, or sailing back out with empty launchers. */
  state: 'in' | 'station' | 'out';
  /** The tick it sails in. */
  at: number;
  wave: number;
}

/** A station's roaming unit: a tank, a helicopter or a fighter that goes after the enemy within reach of home. */
export interface Patrol {
  id: number;
  /** The station it belongs to, by tower id. */
  station: number;
  type: string;
  def: UnitDef;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  cooldown: number;
  burstPos: number;
  targetId: number;
  /** Shot down: the tick it comes back from its station. */
  respawnAt: number;
}

/** A missile in flight from a tower, locked on one enemy for good. */
interface Projectile {
  id: number;
  from: number;
  launchX: number;
  launchY: number;
  targetId: number;
  x: number;
  y: number;
  tx: number;
  ty: number;
  speed: number;
  damage: number;
  vx: number;
  vy: number;
  coasting: boolean;
  coastLeft: number;
  burn: number;
}

/** An enemy still to come: which, and the tick it steps onto the road. */
interface Pending { at: number; unit: string; wave: number }

interface Smoke { x: number; y: number; r: number; until: number }
interface FireMission { at: number; tx: number; ty: number }

/** What happened this tick that the raid's combat feed does not say: for the game's own HUD. */
export type TdEvent =
  | { kind: 'wave'; n: number; wave: TdWave }
  | { kind: 'leak'; type: string; lives: number; id: number }
  | { kind: 'cleared'; n: number; bonus: number }
  | { kind: 'tower-lost'; type: string; id: number }
  | { kind: 'won' }
  | { kind: 'lost' };

export type Phase = 'build' | 'wave' | 'lost';

/**
 * Everything the player does, as the sim records it: with the map and the seed, the log replays
 * to the same game, so a game is saved as its log and restored by playing it back (see `replay`).
 */
export type TdCommand =
  | { tick: number; kind: 'place'; type: string; x: number; y: number }
  | { tick: number; kind: 'upgrade'; id: number }
  | { tick: number; kind: 'sell'; id: number }
  | { tick: number; kind: 'rebuild'; id: number }
  | { tick: number; kind: 'mode'; id: number; mode: TargetMode }
  | { tick: number; kind: 'priority'; id: number; priority: Threat[] }
  | { tick: number; kind: 'wave' }
  | { tick: number; kind: 'ability'; ability: AbilityKind; x: number; y: number; target: number };

/** How far past the road's end an enemy counts as inside the Command Center. */
const HQ_REACH = TILE;
/** How long an interceptor that missed flies on before it blows itself up. */
const SELF_DESTRUCT_TICKS = TICKS_PER_SECOND;
const COAST_MARGIN = TILE * 6;
/** How much quicker or slower than the book each enemy moves, in percent, so a column strings out along the road. */
const PACE_MIN_PCT = 88, PACE_MAX_PCT = 112;

/** How far beyond the map's edge anything airborne enters, in tiles. */
const SKY_APPROACH = 6;

/** A missile this close makes a jinking aircraft break hard. */
const JINK_RANGE = TILE * 6;
const JINK_TICKS = 2 * TICKS_PER_SECOND;
/** A one-way drone dives on a tower this near the road. */
const DIVE_REACH = TILE * 2;
/** What a fortified tower shrugs off: rifles, machine guns and light drone or helicopter fire. */
const SMALL_ARMS: ReadonlySet<Threat> = new Set<Threat>(['infantry', 'drone', 'aircraft']);
/** Ticks between a fire mission's call and its shells landing (the ability's own countdown). */
const ARTILLERY_FLIGHT = 3 * TICKS_PER_SECOND;

const isAir = (def: UnitDef): boolean => def.domain === 'aircraft' || def.domain === 'drone' || def.domain === 'missile';
const isWarhead = (def: UnitDef): boolean => def.warhead !== undefined && def.speed === 0;

/** A blast's damage at `d` units from its centre: full there, half at its edge `r`, nothing past it. */
function splashDamage(damage: number, d: number, r: number): number {
  return d > r ? 0 : damage - Math.floor((damage * d) / (2 * r));
}

function afterShot(e: { cooldown: number; burstPos: number }, def: { attackTicks?: number; burst?: number; burstGap?: number }): void {
  e.burstPos++;
  if (e.burstPos < (def.burst ?? 1)) { e.cooldown = def.burstGap ?? 1; return; }
  e.burstPos = 0;
  e.cooldown = def.attackTicks ?? 1;
}

function fnv(h: number, v: number): number {
  h ^= v & 0xff; h = Math.imul(h, 0x01000193);
  h ^= (v >>> 8) & 0xff; h = Math.imul(h, 0x01000193);
  h ^= (v >>> 16) & 0xff; h = Math.imul(h, 0x01000193);
  h ^= (v >>> 24) & 0xff; h = Math.imul(h, 0x01000193);
  return h >>> 0;
}

export type PlaceError = 'unknown' | 'off-map' | 'on-road' | 'off-road' | 'occupied' | 'poor';

export const PLACE_ERROR_TEXT: Record<PlaceError, string> = {
  unknown: 'Not a tower', 'off-map': 'Off the map', 'on-road': 'Not on the road', 'off-road': 'Mines go on the road', occupied: 'Something is there', poor: 'Not enough cash',
};

export class TdSim {
  readonly config: GameConfig;
  readonly road: Road;
  private readonly rng: Rng;
  tick = 0;
  cash: number;
  readonly maxIntegrity: number;
  /** Waves started so far; `cleared` of them are over. */
  wave = 0;
  cleared = 0;
  phase: Phase = 'build';
  /** The map's waves are held: the game goes on, but it is won. */
  won = false;
  kills = 0;
  leaks = 0;
  /** The towers, the Command Center first (id 0). Ids are indexes and never change. */
  readonly towers: Tower[] = [];
  readonly enemies: Enemy[] = [];
  readonly patrols: Patrol[] = [];
  /** The fleet offshore: the ships of the waves that are on, until their launchers are empty and they have sailed out. */
  readonly ships: Ship[] = [];
  private projectiles: Projectile[] = [];
  private queue: Pending[] = [];
  private smokes: Smoke[] = [];
  private missions: FireMission[] = [];
  private readonly abilityAt: Record<AbilityKind, number> = { artillery: 0, smoke: 0, repair: 0 };
  /** The tower on each tile, or -1. The road is not in here; see road.mask. */
  private readonly occ = new Int16Array(MAP_SIZE * MAP_SIZE).fill(-1);
  /**
   * The next id for anything the snapshot lists as a unit: the enemy, the patrols and the fleet all
   * draw from this one counter, so no two of them ever share an id. The client draws units by id, and
   * when the patrols' ids were a block above the enemy's a long game's enemies grew into that block.
   */
  private nextUnit = 1;
  private nextProjectile = 1;
  private lastAlive = 0;
  private shots: Shot[] = [];
  private events: CombatEvent[] = [];
  /** The game's own events this tick. */
  tdEvents: TdEvent[] = [];
  /** What the player has done, in order: the game's save. */
  readonly log: TdCommand[] = [];
  stateHash = 0x811c9dc5;

  readonly map: TdMap;
  readonly seed: number;

  constructor(map: TdMap, seed: number, config: GameConfig = TD_CONFIG) {
    this.map = map;
    this.seed = seed;
    this.config = config;
    this.road = buildRoad(map);
    this.rng = new Rng(seed);
    this.cash = map.cash;
    this.maxIntegrity = map.integrity;
    const hq = config.buildings.hq!;
    this.towers.push({
      id: 0, type: 'hq', def: hq, x: this.road.hqX, y: this.road.hqY, level: 1, hp: map.integrity, maxHp: map.integrity, alive: true, gone: false,
      cooldown: 0, burstPos: 0, ammo: Infinity, targetId: -1, missed: new Set(), mode: 'first', priority: [], fired: false, firedTick: -1,
    });
    this.occupy(0, this.road.hqX, this.road.hqY, HQ_SIZE);
  }

  /** The Command Center's integrity: what is left of it. */
  get integrity(): number {
    return this.towers[0]!.hp;
  }

  /** The wave with this number, at this map's difficulty: DEFCON 1 goes nuclear. */
  waveAt(n: number): TdWave {
    return tdWave(n, this.map.nuclear);
  }

  /** The wave that is on, or the next one to come. */
  get currentWave(): TdWave {
    return this.waveAt(Math.max(1, this.phase === 'wave' ? this.wave : this.wave + 1));
  }

  /** The enemies still to step onto the road, and what the fleet has still to launch. */
  get remaining(): number {
    return this.queue.length + this.ships.reduce((n, s) => n + s.left, 0);
  }

  // ---- building ------------------------------------------------------------------------

  private occupy(id: number, x: number, y: number, size: number): void {
    for (let dy = 0; dy < size; dy++) for (let dx = 0; dx < size; dx++) {
      const tx = x + dx, ty = y + dy;
      if (tx >= 0 && ty >= 0 && tx < MAP_SIZE && ty < MAP_SIZE) this.occ[ty * MAP_SIZE + tx] = id;
    }
  }

  private vacate(x: number, y: number, size: number): void {
    for (let dy = 0; dy < size; dy++) for (let dx = 0; dx < size; dx++) {
      const tx = x + dx, ty = y + dy;
      if (tx >= 0 && ty >= 0 && tx < MAP_SIZE && ty < MAP_SIZE) this.occ[ty * MAP_SIZE + tx] = -1;
    }
  }

  /** The tower standing on a tile, or -1. */
  towerAt(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= MAP_SIZE || y >= MAP_SIZE) return -1;
    return this.occ[y * MAP_SIZE + x]!;
  }

  isRoad(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < MAP_SIZE && y < MAP_SIZE && this.road.mask[y * MAP_SIZE + x] === 1;
  }

  /** Why a tower cannot go at (x, y), top-left tile, or null if it can. */
  canPlace(type: string, x: number, y: number): PlaceError | null {
    const def = this.config.buildings[type];
    if (!def || !TOWERS.includes(type)) return 'unknown';
    const size = def.size;
    const lo = def.mine ? 0 : PLACE_MARGIN, hi = def.mine ? MAP_SIZE : MAP_SIZE - PLACE_MARGIN;
    if (x < lo || y < lo || x + size > hi || y + size > hi) return 'off-map';
    for (let dy = 0; dy < size; dy++) for (let dx = 0; dx < size; dx++) {
      const tx = x + dx, ty = y + dy;
      if (tx < SEA_WIDTH) return 'off-map';
      if (this.occ[ty * MAP_SIZE + tx] !== -1) return 'occupied';
      const road = this.isRoad(tx, ty);
      if (def.mine ? !road : road) return def.mine ? 'off-road' : 'on-road';
    }
    if (this.cash < (PRICE[type] ?? Infinity)) return 'poor';
    return null;
  }

  /** Places a tower and pays for it. Returns the new tower's id, or the reason it could not go there. */
  place(type: string, x: number, y: number): number | PlaceError {
    const err = this.canPlace(type, x, y);
    if (err) return err;
    const def = this.config.buildings[type]!;
    const id = this.towers.length;
    this.towers.push({
      id, type, def, x, y, level: 1, hp: hpForLevel(def, 1), maxHp: hpForLevel(def, 1), alive: true, gone: false,
      cooldown: 0, burstPos: 0, ammo: def.ammo ?? Infinity, targetId: -1, missed: new Set(), mode: 'first', priority: def.defaultPriority ? [...def.defaultPriority] : [], fired: false, firedTick: -1,
    });
    this.occupy(id, x, y, def.size);
    this.cash -= PRICE[type]!;
    this.log.push({ tick: this.tick, kind: 'place', type, x, y });
    if (def.patrol) this.fieldPatrol(id);
    return id;
  }

  /** The price of the next level, or null when the tower is at the top, rubble, or not a tower. */
  upgradeCost(id: number): number | null {
    const t = this.towers[id];
    if (!t || id === 0 || t.gone || !t.alive || t.level >= MAX_TOWER_LEVEL || t.def.mine) return null;
    return upgradePrice(t.type, t.level + 1);
  }

  upgrade(id: number): boolean {
    const cost = this.upgradeCost(id);
    const t = this.towers[id];
    if (cost === null || !t || this.cash < cost) return false;
    this.cash -= cost;
    this.log.push({ tick: this.tick, kind: 'upgrade', id });
    t.level++;
    const max = hpForLevel(t.def, t.level);
    // the extra hit points come with the level, on top of what it has
    t.hp += max - t.maxHp;
    t.maxHp = max;
    return true;
  }

  /** What selling a tower gives back; rubble sells for the ground it stood on (nothing). */
  sellValue(id: number): number {
    const t = this.towers[id];
    if (!t || id === 0 || t.gone) return 0;
    return t.alive ? sellValue(t.type, t.level) : 0;
  }

  sell(id: number): boolean {
    const t = this.towers[id];
    if (!t || id === 0 || t.gone) return false;
    this.cash += this.sellValue(id);
    this.log.push({ tick: this.tick, kind: 'sell', id });
    for (const p of this.patrols) if (p.station === id) p.alive = false;
    t.gone = true;
    t.alive = false;
    this.vacate(t.x, t.y, t.def.size);
    for (const e of this.enemies) if (e.targetId === id) e.targetId = -1;
    return true;
  }

  rebuildCost(id: number): number | null {
    const t = this.towers[id];
    if (!t || id === 0 || t.gone || t.alive) return null;
    return rebuildPrice(t.type);
  }

  /** Puts a destroyed tower back up, at the level it had. */
  rebuild(id: number): boolean {
    const cost = this.rebuildCost(id);
    const t = this.towers[id];
    if (cost === null || !t || this.cash < cost) return false;
    this.cash -= cost;
    this.log.push({ tick: this.tick, kind: 'rebuild', id });
    t.alive = true;
    t.hp = t.maxHp;
    if (t.def.patrol) this.fieldPatrol(id);
    t.cooldown = 0; t.burstPos = 0; t.targetId = -1; t.missed.clear();
    return true;
  }

  setMode(id: number, mode: TargetMode): void {
    const t = this.towers[id];
    if (t && id !== 0) { t.mode = mode; t.targetId = -1; this.log.push({ tick: this.tick, kind: 'mode', id, mode }); }
  }

  setPriority(id: number, priority: Threat[]): void {
    const t = this.towers[id];
    if (t && id !== 0) { t.priority = [...priority]; t.targetId = -1; this.log.push({ tick: this.tick, kind: 'priority', id, priority: [...priority] }); }
  }

  // ---- waves ---------------------------------------------------------------------------

  /** Sends the next wave. During a wave it stacks on the one that is on, and pays an early-call bonus. */
  startWave(): TdWave | null {
    if (this.phase === 'lost') return null;
    this.log.push({ tick: this.tick, kind: 'wave' });
    const early = this.phase === 'wave';
    this.wave++;
    const wave = this.waveAt(this.wave);
    for (const grp of wave.groups) {
      // each one a little early or late on its mark, up to a third of the gap, so a group does not step out in lockstep
      for (let i = 0; i < grp.count; i++) {
        const jitter = grp.gap >= 3 ? this.rng.int(Math.floor((grp.gap * 2) / 3) + 1) - Math.floor(grp.gap / 3) : 0;
        this.queue.push({ at: Math.max(this.tick, this.tick + grp.at + i * grp.gap + jitter), unit: grp.unit, wave: this.wave });
      }
    }
    this.queue.sort((a, b) => a.at - b.at || a.unit.localeCompare(b.unit));
    this.sail(wave.ships, this.wave);
    if (early) this.cash += Math.floor((waveBonus(this.wave - 1) * EARLY_CALL_BONUS_PCT) / 100);
    if (this.phase === 'build') {
      // a fresh wave: the towers with a magazine start it full
      for (const t of this.towers) if (t.def.ammo !== undefined) { t.ammo = t.def.ammo; t.fired = false; }
    }
    this.phase = 'wave';
    this.lastAlive = this.tick;
    this.tdEvents.push({ kind: 'wave', n: this.wave, wave });
    return wave;
  }

  /**
   * Puts one enemy on the map now: the waves call it on their schedule, the tests out of turn. A
   * warhead appears over its aim with its fuse running: half of them, on the sim's own roll, go for
   * the towers where the blast catches the most hit points, the rest for the Command Center; `aim`
   * forces one or the other. Returns the enemy, or null for a type the config does not know.
   */
  spawn(type: string, aim?: WarheadAim, wave = this.wave): Enemy | null {
    const def = this.config.units[type];
    if (!def) return null;
    const warhead = isWarhead(def);
    // a ship is not an enemy on the road: it is launched from, see `sail`; a map with two roads splits the column between them
    if (def.domain === 'sea') return null;
    const roads = this.road.lanes.length;
    const lane = roads > 1 ? this.rng.int(roads) : 0;
    const [sx, sy] = this.road.lanes[lane]!.pts[0]!;
    const hp = Math.floor((def.hp * armourPct(wave) * this.map.armourPct) / 10000);
    // across the road: infantry use the whole width out to the verges, vehicles most of it, things in the air wander most
    const spread = def.infantry ? Math.floor(TILE * 0.9) : isAir(def) ? Math.floor(TILE * 0.8) : Math.floor(TILE * 0.6);
    const off = this.rng.int(spread * 2 + 1) - spread;
    // and each walks or drives at its own pace, a little quicker or slower than the book, so a column strings out
    const pace = PACE_MIN_PCT + this.rng.int(PACE_MAX_PCT - PACE_MIN_PCT + 1);
    const e: Enemy = {
      id: this.nextUnit++, type, def, s: 0, lane, x: sx, y: sy, hp, maxHp: hp, alive: true, through: false, off,
      cooldown: 0, burstPos: 0, targetId: -1, fuse: warhead ? def.warhead!.fuseTicks : -1,
      spawnX: sx, spawnY: sy, lockX: -1, lockY: -1, jinkTicks: 0, jinkDir: 1, aim: null, landed: false, flyAt: -1, wave, pace,
    };
    if (warhead) this.aimWarhead(e, aim);
    else if (isAir(def)) {
      // anything that flies comes in from any side of the map, free of the road, and heads straight
      // for the Command Center: its spread is across the line of flight, so a flight is not a queue
      const [cx, cy] = this.towerCentre(this.towers[0]!);
      const [ex, ey] = this.skyEntry();
      const d = dist(ex, ey, cx, cy) || 1;
      const ox = Math.trunc((-(cy - ey) * off) / d), oy = Math.trunc(((cx - ex) * off) / d);
      e.x = e.spawnX = ex + ox; e.y = e.spawnY = ey + oy;
      e.lockX = cx + ox; e.lockY = cy + oy;
      e.flyAt = 0;
    } else this.placeOnRoad(e);
    this.enemies.push(e);
    return e;
  }

  /**
   * Where something airborne enters: a point a few tiles beyond a random edge of the map, any
   * side, anywhere along it, so the air comes from every angle and the air defence cannot sit on
   * one line. The sea side counts too: a plane off a carrier comes in over the water.
   */
  private skyEntry(): [x: number, y: number] {
    const side = this.rng.int(4);
    const along = this.rng.int(MAP_SIZE * TILE);
    const out = SKY_APPROACH * TILE;
    switch (side) {
      case 0: return [along, -out];                      // north
      case 1: return [MAP_SIZE * TILE + out, along];     // east
      case 2: return [along, MAP_SIZE * TILE + out];     // south
      default: return [-out, along];                     // west, in off the sea
    }
  }

  /**
   * A warhead appears over its aim with its fuse running: half of them, on the sim's own roll, go for
   * the towers where the blast catches the most hit points, the rest for the Command Center; `aim`
   * forces one or the other. Its arc is drawn from wherever it was spawned (the edge, or a submarine).
   */
  private aimWarhead(e: Enemy, aim?: WarheadAim): void {
    // the roll is taken whatever the aim is forced to, so a forced spawn moves the rng as a free one would
    const roll = this.rng.int(2) === 0;
    const target = (aim ?? (roll ? 'towers' : 'hq')) === 'towers' ? this.towerCluster(e.def.warhead!.radius) : null;
    const [hqX, hqY] = this.towerCentre(this.towers[0]!);
    const [ax, ay] = target ? this.towerCentre(target) : [hqX, hqY];
    e.aim = target ? 'towers' : 'hq';
    e.x = e.lockX = ax;
    e.y = e.lockY = ay;
    this.events.push({ kind: 'inbound', what: e.type, x: ax, y: ay, id: e.id, aim: target ? target.type : 'hq', by: e.launcher });
  }

  /**
   * The tower whose centre a blast of radius `r` should fall on to catch the most tower hit points,
   * the Command Center and the mines not counted, or null when there is nothing standing to hit.
   * Ties go to the one built first. The raid's strikes picked their tile the same way.
   */
  private towerCluster(r: number): Tower | null {
    let best: Tower | null = null, bestHp = 0;
    for (const t of this.towers) {
      if (!t.alive || t.gone || t.id === 0 || t.def.mine) continue;
      const [cx, cy] = this.towerCentre(t);
      let hp = 0;
      for (const u of this.towers) {
        if (!u.alive || u.gone || u.id === 0 || u.def.mine) continue;
        if (distToRect(cx, cy, ...this.towerRect(u)) <= r) hp += u.hp;
      }
      if (hp > bestHp) { bestHp = hp; best = t; }
    }
    return best;
  }

  // ---- the fleet -----------------------------------------------------------------------

  /** The wave's ships put to sea: each sails in from the open water to its own station off the coast when its time comes. */
  private sail(ships: readonly TdShip[], wave: number): void {
    const inX = -(FLEET_STATION + FLEET_APPROACH) * TILE;
    // spread down the coast, a little off their marks so two waves' ships do not sit on one another
    ships.forEach((sh, i) => {
      const def = this.config.units[sh.ship];
      if (!def) return;
      const y = Math.floor((MAP_SIZE * TILE * (i + 1)) / (ships.length + 1)) + this.rng.int(TILE * 6 + 1) - TILE * 3;
      this.ships.push({ id: this.nextUnit++, type: sh.ship, def, x: inX, y, unit: sh.unit, left: sh.count, gap: sh.gap, launchIn: Math.floor(sh.gap / 2), state: 'in', at: this.tick + sh.at, wave });
    });
  }

  /**
   * A ship launches one of what it carries: a missile or drone flies at the Command Center free of
   * any road, and the shield has to stop it; a warhead (a submarine's nuclear ICBM) arcs up out of
   * the sea and comes down on its aim with its fuse running, like one from beyond the map.
   */
  private launchAtHq(ship: Ship): void {
    const def = this.config.units[ship.unit];
    if (!def) return;
    const hq = this.towers[0]!;
    const [cx, cy] = this.towerCentre(hq);
    const warhead = isWarhead(def);
    const m: Enemy = {
      id: this.nextUnit++, type: ship.unit, def, s: 0, lane: 0, x: ship.x, y: ship.y, hp: def.hp, maxHp: def.hp, alive: true, through: false, off: 0,
      cooldown: 0, burstPos: 0, targetId: -1, fuse: warhead ? def.warhead!.fuseTicks : -1, spawnX: ship.x, spawnY: ship.y, lockX: cx, lockY: cy, jinkTicks: 0, jinkDir: 1, aim: null, landed: false,
      flyAt: warhead ? -1 : 0, wave: ship.wave, launcher: ship.type, pace: 100,
    };
    this.enemies.push(m);
    this.shots.push({ by: 'unit', type: 'launch', fromX: ship.x, fromY: ship.y, toX: ship.x, toY: ship.y });
    if (warhead) this.aimWarhead(m);
    else this.events.push({ kind: 'inbound', what: ship.unit, x: cx, y: cy, by: ship.type, id: m.id, aim: 'hq' });
  }

  private stepShip(sh: Ship): void {
    if (this.tick < sh.at) return;
    const speed = Math.floor((FLEET_SPEED_TPS * TILE) / TICKS_PER_SECOND);
    const station = -FLEET_STATION * TILE;
    if (sh.state === 'in') {
      sh.x = Math.min(station, sh.x + speed);
      if (sh.x >= station) sh.state = 'station';
      return;
    }
    if (sh.state === 'station') {
      if (sh.left <= 0) { sh.state = 'out'; return; }
      if (--sh.launchIn > 0) return;
      this.launchAtHq(sh);
      sh.left--;
      sh.launchIn = sh.gap;
      return;
    }
    sh.x -= speed;
  }

  private placeOnRoad(e: Enemy): void {
    const [px, py, leg] = roadPoint(this.road, e.s, e.lane);
    const [dx, dy] = legDir(this.road, leg, e.lane);
    // the offset is across the road: the leg's direction turned a quarter
    e.x = px + Math.trunc((-dy * e.off) / 1000);
    e.y = py + Math.trunc((dx * e.off) / 1000);
  }

  // ---- abilities -----------------------------------------------------------------------

  abilityReadyIn(kind: AbilityKind): number {
    return Math.max(0, this.abilityAt[kind] - this.tick);
  }

  /** Calls an ability in: a fire mission or smoke on a tile, or a repair on a tower. Returns whether it was taken. */
  ability(kind: AbilityKind, x = 0, y = 0, target = -1): boolean {
    const def = this.config.abilities[kind];
    if (this.phase === 'lost' || this.abilityReadyIn(kind) > 0) return false;
    if (def.aim === 'tile') {
      if (x < 0 || y < 0 || x >= MAP_SIZE || y >= MAP_SIZE) return false;
      const cx = x * TILE + TILE / 2, cy = y * TILE + TILE / 2;
      if (kind === 'artillery') this.missions.push({ at: this.tick + ARTILLERY_FLIGHT, tx: cx, ty: cy });
      else { this.smokes.push({ x: cx, y: cy, r: def.radius ?? TILE * 3, until: this.tick + (def.lastsTicks ?? 0) }); this.shots.push({ by: 'strike', type: 'smoke', fromX: cx, fromY: cy, toX: cx, toY: cy }); }
    } else {
      const t = this.towers[target];
      if (!t || t.gone || !t.alive || t.hp >= t.maxHp) return false;
      t.hp = Math.min(t.maxHp, t.hp + Math.floor((t.maxHp * (def.repairPct ?? 0)) / 100));
      const cx = t.x * TILE + (t.def.size * TILE) / 2, cy = t.y * TILE + (t.def.size * TILE) / 2;
      this.shots.push({ by: 'strike', type: 'repair', fromX: cx, fromY: cy, toX: cx, toY: cy });
    }
    this.abilityAt[kind] = this.tick + def.cooldownTicks;
    this.log.push({ tick: this.tick, kind: 'ability', ability: kind, x, y, target });
    this.event('ability', kind, x * TILE + TILE / 2, y * TILE + TILE / 2);
    return true;
  }

  private blinded(e: Enemy): boolean {
    for (const c of this.smokes) if (dist(e.x, e.y, c.x, c.y) <= c.r) return true;
    return false;
  }

  // ---- the enemy -----------------------------------------------------------------------

  private towerRect(t: Tower): [number, number, number, number] {
    return [t.x * TILE, t.y * TILE, t.def.size * TILE, t.def.size * TILE];
  }

  private towerCentre(t: Tower): [number, number] {
    return [t.x * TILE + (t.def.size * TILE) / 2, t.y * TILE + (t.def.size * TILE) / 2];
  }

  private hitTower(t: Tower, damage: number, by: string): void {
    if (!t.alive) return;
    t.hp -= Math.max(1, Math.floor((damage * TOWER_DAMAGE_PCT) / 100));
    if (t.hp > 0) return;
    t.hp = 0;
    t.alive = false;
    t.targetId = -1;
    const [cx, cy] = this.towerCentre(t);
    this.event('destroyed', t.type, cx, cy, by, t.id);
    this.tdEvents.push({ kind: 'tower-lost', type: t.type, id: t.id });
    for (const e of this.enemies) if (e.targetId === t.id) e.targetId = -1;
    for (const p of this.projectiles) if (p.from === t.id) { /* a missile already flying still flies */ }
  }

  /** A blast on the ground at (x, y): the towers in reach take it, the full damage at the centre and half at the edge. */
  private blastTowers(x: number, y: number, r: number, damage: number, by: string): void {
    for (const t of this.towers) {
      if (!t.alive || t.gone || t.id === 0) continue;
      const dmg = splashDamage(damage, distToRect(x, y, ...this.towerRect(t)), r);
      if (dmg > 0) this.hitTower(t, dmg, by);
    }
  }

  /** A tower's blast at (x, y): the enemy round the target, in the air or on the ground as the target was. */
  private blastEnemies(x: number, y: number, r: number, damage: number, except: Enemy | null, air: boolean): void {
    for (const e of this.enemies) {
      if (!e.alive || e === except || isAir(e.def) !== air || e.fuse >= 0) continue;
      const dmg = splashDamage(damage, dist(x, y, e.x, e.y), r);
      if (dmg > 0) {
        e.hp -= dmg;
        if (e.hp <= 0) {
          e.hp = 0; e.alive = false;
          if (isAir(e.def)) this.event(e.def.domain === 'missile' ? 'intercepted' : 'downed', e.type, e.x, e.y, undefined, e.id);
        }
      }
    }
  }

  private stepEnemy(e: Enemy): void {
    const def = e.def;
    if (e.fuse >= 0) {
      // a warhead coming down on its aim: the Command Center, which it costs integrity, or the towers under it
      if (--e.fuse > 0) return;
      e.alive = false;
      if (e.aim === 'towers') e.landed = true; else e.through = true;
      const w = def.warhead!;
      this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: e.x, toY: e.y });
      this.event('impact', e.type, e.x, e.y, undefined, e.id);
      if (w.radius > 0) this.blastTowers(e.x, e.y, w.radius, w.damage, e.type);
      return;
    }
    if (e.jinkTicks > 0) e.jinkTicks--;
    if (e.cooldown > 0) e.cooldown--;
    const speed = Math.floor((def.speed * ENEMY_SPEED_PCT * e.pace) / 10000);
    if (e.flyAt >= 0) {
      // in the air, off the road or off a ship: straight at what it was aimed at, shooting what it passes
      // on the way. `s` is how far it has flown, so a gun set to First takes the one nearest home.
      const d = dist(e.x, e.y, e.lockX, e.lockY);
      e.s += speed;
      if (d > speed) { e.x += Math.trunc(((e.lockX - e.x) * speed) / d); e.y += Math.trunc(((e.lockY - e.y) * speed) / d); }
      else {
        e.x = e.lockX; e.y = e.lockY;
        e.alive = false;
        if (e.flyAt === 0) { e.through = true; return; }   // into the Command Center: a leak, costing it integrity
        e.landed = true;
        this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: e.x, toY: e.y });
        this.event('impact', e.type, e.x, e.y, undefined, e.id);
        this.blastTowers(e.x, e.y, def.splash ?? TILE, def.damage, e.type);
        return;
      }
    } else {
      e.s += speed;
      if (e.s >= this.road.lanes[e.lane]!.length - HQ_REACH) { e.alive = false; e.through = true; return; }
      this.placeOnRoad(e);
    }
    if (def.damage <= 0 || def.recon) return;
    const blind = this.blinded(e);
    // a one-way drone dives on the first tower it finds under its path: that is what it is for
    if (def.oneWay && def.domain === 'drone') {
      if (blind) return;
      for (const t of this.towers) {
        if (!t.alive || t.gone || t.id === 0 || t.def.mine) continue;
        if (distToRect(e.x, e.y, ...this.towerRect(t)) > DIVE_REACH) continue;
        const [cx, cy] = this.towerCentre(t);
        this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: cx, toY: cy });
        this.hitTower(t, def.damage, e.type);
        if (def.splash) this.blastTowers(cx, cy, def.splash, def.damage, e.type);
        e.alive = false;
        this.event('detonated', e.type, cx, cy, undefined, e.id);
        return;
      }
      return;
    }
    if (def.oneWay) return;   // a cruise missile flies on to the Command Center
    if (e.cooldown > 0 || blind) return;
    // anything else shoots the nearest tower in reach as it goes by, without stopping
    let target = e.targetId >= 0 ? this.towers[e.targetId]! : null;
    if (target && (!target.alive || target.gone || distToRect(e.x, e.y, ...this.towerRect(target)) > def.range)) target = null;
    if (!target) {
      let bestD = def.range + 1;
      for (const t of this.towers) {
        if (!t.alive || t.gone || t.id === 0 || t.def.mine) continue;
        const d = distToRect(e.x, e.y, ...this.towerRect(t));
        if (d < bestD) { bestD = d; target = t; }
      }
      e.targetId = target ? target.id : -1;
    }
    // a patrol nearer than any tower draws the fire instead
    let patrol: Patrol | null = null, patrolD = target ? distToRect(e.x, e.y, ...this.towerRect(target)) : def.range + 1;
    for (const p of this.patrols) {
      if (!p.alive || !this.canHurtPatrol(def, p)) continue;
      const d = dist(e.x, e.y, p.x, p.y);
      if (d <= def.range && d < patrolD) { patrolD = d; patrol = p; }
    }
    if (patrol) {
      this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: patrol.x, toY: patrol.y });
      const dmg = threatOf(patrol.def) === 'vehicle' && def.antiArmor ? def.damage * def.antiArmor : def.damage;
      this.hitPatrol(patrol, dmg, e.type);
      afterShot(e, def);
      return;
    }
    if (!target) return;
    const [cx, cy] = this.towerCentre(target);
    this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: cx, toY: cy });
    let damage = def.damage;
    const threat = threatOf(def);
    // concrete shrugs off small arms; a marksman picks off the crews of anything manned
    if (target.def.fortified && SMALL_ARMS.has(threat) && !def.antiArmor) damage = Math.floor(damage / 2);
    if (def.sniper && target.def.manned) damage *= 2;
    this.hitTower(target, damage, e.type);
    if (def.splash) this.blastTowers(cx, cy, def.splash, Math.floor(damage / 2), e.type);
    afterShot(e, def);
  }

  // ---- the patrols ---------------------------------------------------------------------

  /** A station's unit, out on the field, or back at it after being shot down. */
  private fieldPatrol(station: number): void {
    const t = this.towers[station]!;
    const unit = t.def.patrol!.unit;
    const def = this.config.units[unit];
    if (!def) return;
    const [cx, cy] = this.towerCentre(t);
    const old = this.patrols.find(p => p.station === station);
    if (old) { old.alive = true; old.hp = old.maxHp; old.x = cx; old.y = cy; old.cooldown = 0; old.burstPos = 0; old.targetId = -1; return; }
    this.patrols.push({ id: this.nextUnit++, station, type: unit, def, x: cx, y: cy, hp: def.hp, maxHp: def.hp, alive: true, cooldown: 0, burstPos: 0, targetId: -1, respawnAt: -1 });
  }

  /** Whether an enemy's weapon can reach a patrol: a marksman's rifle is no use against armour, a tank's gun cannot be raised at a helicopter. */
  private canHurtPatrol(def: UnitDef, p: Patrol): boolean {
    const threat = threatOf(p.def);
    if (def.cannotHit?.includes(threat)) return false;
    if (isAir(p.def) && !isAir(def) && !def.infantry && def.domain !== 'sea') return false;
    return true;
  }

  private hitPatrol(p: Patrol, damage: number, by: string): void {
    if (!p.alive) return;
    p.hp -= damage;
    if (p.hp > 0) return;
    p.hp = 0;
    p.alive = false;
    p.respawnAt = this.tick + PATROL_RESPAWN_TICKS;
    this.event(isAir(p.def) ? 'downed' : 'destroyed', p.type, p.x, p.y, by, p.id);
    for (const e of this.enemies) if (e.targetId === p.id) e.targetId = -1;
  }

  private stepPatrol(p: Patrol): void {
    const st = this.towers[p.station]!;
    if (!st.alive || st.gone) { p.alive = false; p.respawnAt = -1; return; }
    if (!p.alive) {
      if (p.respawnAt >= 0 && this.tick >= p.respawnAt) this.fieldPatrol(p.station);
      return;
    }
    const def = p.def;
    if (p.cooldown > 0) p.cooldown--;
    const [hx, hy] = this.towerCentre(st);
    const radius = st.def.patrol!.radius;
    const reach = radius + def.range;
    const targets = PATROL_TARGETS[p.type] ?? ['infantry', 'vehicle'];
    const usable = (e: Enemy): boolean => e.alive && e.fuse < 0 && targets.includes(threatOf(e.def)) && dist(hx, hy, e.x, e.y) <= reach;
    let target = p.targetId >= 0 ? this.enemies.find(e => e.id === p.targetId) ?? null : null;
    if (target && !usable(target)) target = null;
    if (!target) {
      let bestD = Infinity;
      for (const e of this.enemies) {
        if (!usable(e)) continue;
        const d = dist(p.x, p.y, e.x, e.y);
        if (d < bestD) { bestD = d; target = e; }
      }
      p.targetId = target ? target.id : -1;
    }
    const speed = Math.min(PATROL_MAX_SPEED, def.speed);
    // towards the target until the gun bears, else home; never past the edge of its ground
    const [gx, gy] = target && dist(p.x, p.y, target.x, target.y) > Math.floor((def.range * 9) / 10) ? [target.x, target.y] : target ? [p.x, p.y] : [hx, hy];
    const d = dist(p.x, p.y, gx, gy);
    if (d > speed) {
      const nx = p.x + Math.trunc(((gx - p.x) * speed) / d), ny = p.y + Math.trunc(((gy - p.y) * speed) / d);
      if (dist(hx, hy, nx, ny) <= radius) { p.x = nx; p.y = ny; }
      else { const dh = dist(p.x, p.y, hx, hy); if (dh > speed) { p.x += Math.trunc(((hx - p.x) * speed) / dh); p.y += Math.trunc(((hy - p.y) * speed) / dh); } }
    } else if (d > 0 && !target) { p.x = gx; p.y = gy; }
    if (!target || p.cooldown !== 0 || dist(p.x, p.y, target.x, target.y) > def.range) return;
    this.shots.push({ by: 'defence', type: p.type, fromX: p.x, fromY: p.y, toX: target.x, toY: target.y });
    const dmg = threatOf(target.def) === 'vehicle' && def.antiArmor ? def.damage * def.antiArmor : def.damage;
    target.hp -= dmg;
    if (def.splash) this.blastEnemies(target.x, target.y, def.splash, dmg, target, isAir(target.def));
    if (target.hp <= 0) {
      target.hp = 0; target.alive = false; p.targetId = -1;
      if (isAir(target.def)) this.event(target.def.domain === 'missile' ? 'intercepted' : 'downed', target.type, target.x, target.y, p.type, target.id);
    }
    afterShot(p, def);
  }

  // ---- the towers ----------------------------------------------------------------------

  private canTarget(def: BuildingDef, e: Enemy): boolean {
    return (def.targets ?? ['infantry', 'vehicle']).includes(threatOf(e.def));
  }

  private inFlightAt(id: number): number {
    let n = 0;
    for (const p of this.projectiles) if (!p.coasting && p.targetId === id) n++;
    return n;
  }

  private firedOn(e: Enemy): void {
    if (!e.def.evasion || e.jinkTicks > 0) return;
    e.jinkTicks = JINK_TICKS;
    e.jinkDir = -e.jinkDir;
  }

  private evades(e: Enemy, by: string): boolean {
    if (!e.def.evasion || e.jinkTicks <= 0) return false;
    if (this.rng.int(1000) >= Math.round(e.def.evasion * 1000)) return false;
    this.event('evaded', e.type, e.x, e.y, by, e.id);
    return true;
  }

  /** A round or missile reaching its target: 'hit', or 'miss' (it flies on past). */
  private resolveHit(t: Tower, e: Enemy, damage: number): 'hit' | 'miss' {
    const def = t.def;
    if (def.projectile && this.evades(e, t.type)) return 'miss';
    const chance = interceptChanceVs(def, threatOf(e.def));
    const hits = chance === undefined || this.rng.int(1000) < Math.round(chance * 1000);
    if (hits) {
      e.hp -= damage;
      if (def.splash) this.blastEnemies(e.x, e.y, def.splash, damage, e, isAir(e.def));
    } else if (def.projectile) {
      t.missed.add(e.id);
      if (t.targetId === e.id) t.targetId = -1;
      if (e.def.domain === 'missile') this.event('missed', e.type, e.x, e.y, t.type, e.id);
    }
    if (e.hp <= 0) {
      e.hp = 0; e.alive = false;
      if (t.targetId === e.id) t.targetId = -1;
      if (isAir(e.def)) this.event(e.def.domain === 'missile' ? 'intercepted' : 'downed', e.type, e.x, e.y, t.type, e.id);
    }
    return hits ? 'hit' : 'miss';
  }

  private garrisonOf(t: Tower): number {
    return Math.ceil((t.hp * t.def.garrison!) / t.maxHp);
  }

  private stepTower(t: Tower): void {
    const def = t.def;
    if (!t.alive || t.gone || def.damage === undefined || def.range === undefined || def.attackTicks === undefined || def.mine) return;
    if (t.cooldown > 0) t.cooldown--;
    const [cx, cy] = this.towerCentre(t);
    const range = rangeAt(def, t.level);
    const saturated = (e: Enemy): boolean => def.salvo !== undefined && this.inFlightAt(e.id) >= def.salvo;
    const usable = (e: Enemy, d: number): boolean =>
      e.alive && this.canTarget(def, e) && !t.missed.has(e.id) && d <= range && d >= (def.minRange ?? 0) && !saturated(e);
    let target = t.targetId >= 0 ? this.enemies.find(e => e.id === t.targetId) ?? null : null;
    if (target && !usable(target, dist(cx, cy, target.x, target.y))) target = null;
    // each new trigger pull picks again, so a gun set to First follows the front of the column
    if (t.burstPos === 0 && t.cooldown === 0) target = null;
    if (!target) {
      let bestKey = Infinity, bestRank = 0x7fffffff, bestD = Infinity;
      for (const e of this.enemies) {
        const d = dist(cx, cy, e.x, e.y);
        if (!usable(e, d)) continue;
        const r = t.priority.indexOf(threatOf(e.def));
        const rank = r < 0 ? t.priority.length : r;
        // a warhead is always first: it is seconds from landing
        const key = e.fuse >= 0 ? -0x3fffffff : t.mode === 'first' ? -e.s : t.mode === 'last' ? e.s : t.mode === 'strong' ? -e.hp : d;
        if (rank < bestRank || (rank === bestRank && (key < bestKey || (key === bestKey && d < bestD)))) { bestRank = rank; bestKey = key; bestD = d; target = e; }
      }
      t.targetId = target ? target.id : -1;
    }
    if (!target || t.cooldown !== 0 || t.ammo <= 0) return;
    const damage = damageAt(def, t.level);
    const shooters = def.garrison ? this.garrisonOf(t) : 1;
    for (let k = 0; k < shooters; k++) {
      const off = shooters > 1 ? Math.trunc(((k * 2 - (shooters - 1)) * TILE) / 5) : 0;
      this.shots.push({ by: 'defence', type: t.type, fromX: cx + off, fromY: cy - off, toX: target.x, toY: target.y });
    }
    if (!def.projectile) this.firedOn(target);
    const fired = this.shots.length - shooters;
    t.ammo--;
    t.fired = true;
    t.firedTick = this.tick;
    afterShot(t, def);
    if (def.projectile) {
      this.projectiles.push({ id: this.nextProjectile++, from: t.id, launchX: cx, launchY: cy, targetId: target.id, x: cx, y: cy, tx: target.x, ty: target.y, speed: def.projectile, damage, vx: 0, vy: 0, coasting: false, coastLeft: 0, burn: Math.ceil((range * 2) / def.projectile) + TICKS_PER_SECOND });
      if (saturated(target)) t.targetId = -1;
      return;
    }
    if (this.resolveHit(t, target, damage * shooters) === 'miss') for (let k = fired; k < this.shots.length; k++) if (this.shots[k]!.type === t.type) this.shots[k]!.miss = true;
    if (def.ceaseOnKill && !target.alive) { t.burstPos = 0; t.cooldown = def.attackTicks; }
  }

  private stepMine(t: Tower): void {
    const def = t.def;
    if (!t.alive || t.gone || !def.mine || def.range === undefined) return;
    const [cx, cy] = this.towerCentre(t);
    for (const e of this.enemies) {
      if (!e.alive || e.fuse >= 0 || isAir(e.def) || !this.canTarget(def, e) || dist(cx, cy, e.x, e.y) > def.range) continue;
      this.shots.push({ by: 'defence', type: t.type, fromX: cx, fromY: cy, toX: cx, toY: cy });
      e.hp -= def.damage ?? 0;
      if (e.hp <= 0) { e.hp = 0; e.alive = false; }
      this.blastEnemies(cx, cy, def.mine.splash, def.damage ?? 0, e, false);
      t.alive = false;
      t.gone = true;
      this.vacate(t.x, t.y, def.size);
      this.event('detonated', t.type, cx, cy, t.type, t.id);
      return;
    }
  }

  private stepProjectiles(): void {
    const still: Projectile[] = [];
    const lo = -COAST_MARGIN, hi = MAP_SIZE * TILE + COAST_MARGIN;
    for (const p of this.projectiles) {
      if (p.coasting) {
        if (--p.coastLeft < 0) { this.shots.push({ by: 'defence', type: 'selfdestruct', fromX: p.x, fromY: p.y, toX: p.x, toY: p.y }); continue; }
        p.x += p.vx; p.y += p.vy;
        if (p.x >= lo && p.x <= hi && p.y >= lo && p.y <= hi) still.push(p);
        continue;
      }
      const e = this.enemies.find(u => u.id === p.targetId);
      const from = this.towers[p.from]!;
      if (--p.burn < 0) {
        this.shots.push({ by: 'defence', type: 'fizzle', fromX: p.x, fromY: p.y, toX: p.x, toY: p.y });
        if (e?.alive) this.event('missed', e.type, e.x, e.y, from.type, e.id);
        continue;
      }
      if (e?.alive) { p.tx = e.x; p.ty = e.y; }
      if (e?.alive && dist(p.x, p.y, e.x, e.y) <= JINK_RANGE) this.firedOn(e);
      const d = dist(p.x, p.y, p.tx, p.ty);
      if (d > p.speed) {
        p.vx = Math.trunc(((p.tx - p.x) * p.speed) / d);
        p.vy = Math.trunc(((p.ty - p.y) * p.speed) / d);
        p.x += p.vx; p.y += p.vy;
        still.push(p);
        continue;
      }
      if (d > 0) { p.vx = Math.trunc(((p.tx - p.x) * p.speed) / d); p.vy = Math.trunc(((p.ty - p.y) * p.speed) / d); }
      p.x = p.tx; p.y = p.ty;
      const outcome = e?.alive ? this.resolveHit(from, e, p.damage) : 'miss';
      if (outcome === 'hit') { this.shots.push({ by: 'defence', type: 'impact', fromX: p.x, fromY: p.y, toX: p.x, toY: p.y }); continue; }
      p.coasting = true;
      p.coastLeft = SELF_DESTRUCT_TICKS;
      if (p.vx === 0 && p.vy === 0) p.vx = p.speed;
      still.push(p);
    }
    this.projectiles = still;
  }

  private stepMissions(): void {
    const def = this.config.abilities.artillery;
    const keep: FireMission[] = [];
    for (const m of this.missions) {
      if (m.at > this.tick) { keep.push(m); continue; }
      this.shots.push({ by: 'strike', type: 'artillery', fromX: m.tx, fromY: m.ty, toX: m.tx, toY: m.ty });
      this.event('shelled', 'artillery', m.tx, m.ty);
      const r = def.radius ?? TILE * 3, dmg = def.damage ?? 0;
      for (const e of this.enemies) {
        if (!e.alive || isAir(e.def) || e.fuse >= 0) continue;
        const d = splashDamage(dmg, dist(m.tx, m.ty, e.x, e.y), r);
        if (d > 0) { e.hp -= d; if (e.hp <= 0) { e.hp = 0; e.alive = false; } }
      }
    }
    this.missions = keep;
    this.smokes = this.smokes.filter(c => c.until > this.tick);
  }

  private event(kind: CombatEvent['kind'], what: string, x: number, y: number, by?: string, id?: number): void {
    this.events.push({ kind, what, x, y, ...(by ? { by } : {}), ...(id !== undefined ? { id } : {}) });
  }

  // ---- the loop ------------------------------------------------------------------------

  /** One tick. Returns true once the Command Center has fallen. */
  step(): boolean {
    if (this.phase === 'lost') return true;
    this.tick++;
    this.shots = [];
    this.events = [];
    this.tdEvents = [];
    while (this.queue.length > 0 && this.queue[0]!.at <= this.tick) { const q = this.queue.shift()!; this.spawn(q.unit, undefined, q.wave); }
    for (const sh of this.ships) this.stepShip(sh);
    for (const e of this.enemies) if (e.alive) this.stepEnemy(e);
    for (const t of this.towers) if (t.def.mine) this.stepMine(t); else this.stepTower(t);
    for (const p of this.patrols) this.stepPatrol(p);
    this.stepProjectiles();
    this.stepMissions();
    // the fallen pay, the leaks cost
    const hq = this.towers[0]!;
    for (const e of this.enemies) {
      if (e.alive) continue;
      if (e.through) {
        const lives = BREACH[e.type] ?? 1;
        hq.hp = Math.max(0, hq.hp - lives);
        this.leaks++;
        this.tdEvents.push({ kind: 'leak', type: e.type, lives, id: e.id });
      } else if (!e.landed) {
        this.cash += Math.max(1, Math.floor(((REWARD[e.type] ?? 1) * rewardPct(e.wave)) / 100));
        this.kills++;
      }
    }
    for (let i = this.enemies.length - 1; i >= 0; i--) if (!this.enemies[i]!.alive) this.enemies.splice(i, 1);
    // a ship with empty launchers sails out, and is gone once it is back where it came in
    const gone = -(FLEET_STATION + FLEET_APPROACH) * TILE;
    for (let i = this.ships.length - 1; i >= 0; i--) if (this.ships[i]!.state === 'out' && this.ships[i]!.x <= gone) this.ships.splice(i, 1);
    if (this.enemies.length > 0 || this.queue.length > 0 || this.ships.some(sh => sh.left > 0)) this.lastAlive = this.tick;
    if (hq.hp <= 0) {
      this.phase = 'lost';
      this.tdEvents.push({ kind: 'lost' });
      this.hashState();
      return true;
    }
    if (this.phase === 'wave' && this.queue.length === 0 && this.enemies.length === 0 && this.ships.every(sh => sh.left === 0) && this.projectiles.every(p => p.coasting) && this.tick >= this.lastAlive + WAVE_QUIET_TICKS) {
      // every wave that was on is over at once
      while (this.cleared < this.wave) {
        this.cleared++;
        const bonus = waveBonus(this.cleared);
        this.cash += bonus;
        this.tdEvents.push({ kind: 'cleared', n: this.cleared, bonus });
      }
      this.phase = 'build';
      for (const t of this.towers) t.targetId = -1;
      if (!this.won && this.cleared >= this.map.waves) { this.won = true; this.tdEvents.push({ kind: 'won' }); }
    }
    this.hashState();
    return false;
  }

  private hashState(): void {
    let h = fnv(this.stateHash, this.tick);
    h = fnv(h, this.cash);
    for (const e of this.enemies) { h = fnv(h, e.x); h = fnv(h, e.y); h = fnv(h, e.hp); }
    for (const t of this.towers) { h = fnv(h, t.hp); h = fnv(h, t.gone ? 1 : 0); }
    for (const p of this.patrols) { h = fnv(h, p.x); h = fnv(h, p.y); h = fnv(h, p.hp); }
    for (const sh of this.ships) { h = fnv(h, sh.x); h = fnv(h, sh.y); h = fnv(h, sh.left); }
    for (const p of this.projectiles) { h = fnv(h, p.x); h = fnv(h, p.y); }
    this.stateHash = h;
  }

  /**
   * A game played back from its log: the same map, seed and clicks give the same game, so this is
   * how a saved game comes back. Plays to `tick` (or to the last command when it is sooner).
   */
  static replay(map: TdMap, seed: number, log: readonly TdCommand[], tick: number, config: GameConfig = TD_CONFIG): TdSim {
    const sim = new TdSim(map, seed, config);
    for (const c of log) {
      while (sim.tick < c.tick && sim.phase !== 'lost') sim.step();
      switch (c.kind) {
        case 'place': sim.place(c.type, c.x, c.y); break;
        case 'upgrade': sim.upgrade(c.id); break;
        case 'sell': sim.sell(c.id); break;
        case 'rebuild': sim.rebuild(c.id); break;
        case 'mode': sim.setMode(c.id, c.mode); break;
        case 'priority': sim.setPriority(c.id, c.priority); break;
        case 'wave': sim.startWave(); break;
        case 'ability': sim.ability(c.ability, c.x, c.y, c.target); break;
      }
    }
    while (sim.tick < tick && sim.phase !== 'lost') sim.step();
    return sim;
  }

  /** Ticks of reload left on something, as the share still to go and the whole length, for the bars. */
  private static reloadOf(cooldown: number, total: number): { reload?: number; reloadTicks?: number } {
    return cooldown > 0 && total > 0 ? { reload: Math.min(1, cooldown / total), reloadTicks: total } : {};
  }

  snapshot(): Snapshot {
    const units: UnitSnapshot[] = this.enemies.map(e => ({
      id: e.id, type: e.type, x: e.x, y: e.y, hp: e.hp, maxHp: e.maxHp, disabled: false,
      ...TdSim.reloadOf(e.burstPos === 0 ? e.cooldown : 0, e.def.attackTicks),
      ...(e.fuse >= 0 ? { fuse: e.fuse, fuseTicks: e.def.warhead!.fuseTicks, arc: { fromX: e.spawnX, fromY: e.spawnY, toX: e.lockX, toY: e.lockY } } : {}),
      // a missile off a ship is drawn on an arc from its launcher; a drone flies in low like any other
      ...(e.flyAt >= 0 && e.def.domain === 'missile' ? { arc: { fromX: e.spawnX, fromY: e.spawnY, toX: e.lockX, toY: e.lockY } } : {}),
    }));
    // the fleet, drawn with the enemy: ships nothing can hurt have all their hit points, always
    for (const sh of this.ships) if (this.tick >= sh.at) units.push({ id: sh.id, type: sh.type, x: sh.x, y: sh.y, hp: sh.def.hp, maxHp: sh.def.hp, disabled: false });
    return {
      tick: this.tick,
      incoming: this.missions.map(m => ({ kind: 'artillery' as const, x: Math.floor(m.tx / TILE), y: Math.floor(m.ty / TILE), left: m.at - this.tick })),
      clouds: this.smokes.map(c => ({ x: c.x, y: c.y, radius: c.r, left: c.until - this.tick })),
      units,
      patrols: this.patrols.filter(p => p.alive).map(p => ({
        id: p.id, type: p.type, x: p.x, y: p.y, hp: p.hp, maxHp: p.maxHp, disabled: false, defender: true,
        ...TdSim.reloadOf(p.burstPos === 0 ? p.cooldown : 0, p.def.attackTicks),
      })),
      buildings: this.towers.filter(t => !t.gone).map(t => ({
        id: t.id, type: t.type, x: t.x, y: t.y, size: t.def.size, hp: t.hp, maxHp: t.maxHp, disabled: false,
        ...TdSim.reloadOf(t.burstPos === 0 ? t.cooldown : 0, t.def.attackTicks ?? 0),
        ...(t.ammo <= 0 ? { outOfAmmo: true } : {}),
        ...(Number.isFinite(t.ammo) ? { ammo: t.ammo } : {}),
        // the player's own mines are no secret to them
        ...(t.def.mine ? { revealed: true } : {}),
        ...(t.fired ? { fired: true } : {}),
        ...(t.def.garrison ? { garrison: this.garrisonOf(t) } : {}),
      })),
      shots: this.shots,
      events: this.events,
      projectiles: this.projectiles.map(p => ({
        id: p.id, type: this.towers[p.from]!.type, x: p.x, y: p.y, targetId: p.targetId,
        arc: { fromX: p.launchX, fromY: p.launchY, toX: p.tx, toY: p.ty },
        ...(p.coasting ? { coasting: true } : {}),
      })),
    };
  }
}
